import { Link } from "@tanstack/react-router";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { BOOKING_URL, CONTACT_PATH, routeTo } from "@/lib/site";
import { PHOTO_SIZES, PHOTOS } from "../images";

/** Interior hero matching About Us treatment. */
export function RoomsHero() {
  const photo = PHOTOS.rvSite;

  return (
    <section
      className="relative isolate bg-forest-deep pb-16 sm:pb-20 lg:pb-24"
      aria-labelledby="rooms-hero-title"
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <img
          src={photo.src}
          srcSet={photo.srcSet}
          sizes={PHOTO_SIZES.fullBleed}
          alt={photo.alt}
          width={photo.width}
          height={photo.height}
          fetchPriority="high"
          decoding="async"
          className="hero-zoom size-full object-cover object-center"
        />
        <div
          className="absolute inset-0 bg-[linear-gradient(180deg,oklch(0.259_0.042_163.6/0.9)_0%,oklch(0.259_0.042_163.6/0.68)_42%,oklch(0.259_0.042_163.6/0.92)_100%)]"
          aria-hidden="true"
        />
      </div>

      <div className="mx-auto max-w-[1400px] px-4 pt-10 sm:px-6 sm:pt-14 lg:px-10 lg:pt-16">
        <div className="hero-stagger max-w-3xl">
          <div style={{ "--stagger": 0 } as React.CSSProperties}>
            <Breadcrumb>
              <BreadcrumbList className="text-cream/75">
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/" className="link-underline text-cream/80 hover:text-cream">
                      Home
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="text-cream/45 [&>svg]:size-3.5">
                  <ChevronRight />
                </BreadcrumbSeparator>
                <BreadcrumbItem>
                  <BreadcrumbPage className="font-medium text-cream">
                    Rooms and Parks
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>

          <p className="eyebrow mt-8 text-sand" style={{ "--stagger": 1 } as React.CSSProperties}>
            Your Destination Starts Here
          </p>

          <h1
            id="rooms-hero-title"
            className="mt-4 text-balance font-display text-3xl leading-[1.08] text-cream sm:text-4xl lg:text-[3.35rem]"
            style={{ "--stagger": 2 } as React.CSSProperties}
          >
            RV Sites, Cabins and Comfortable Rooms in Zapata, Texas
          </h1>

          <p
            className="mt-5 max-w-2xl text-base leading-relaxed text-cream/85 sm:text-lg"
            style={{ "--stagger": 3 } as React.CSSProperties}
          >
            Whether you are planning a fishing trip, family gathering, corporate stay, seasonal
            visit, or relaxing South Texas getaway, Amigo Inn &amp; RV Park offers comfortable
            accommodations and full-service camping options throughout the year.
          </p>

          <div
            className="mt-8 flex flex-wrap items-center gap-4"
            style={{ "--stagger": 4 } as React.CSSProperties}
          >
            <Button asChild variant="book" size="ctaLg">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                Check Availability <ArrowUpRight aria-hidden="true" />
              </a>
            </Button>
            <Button asChild variant="outlineLight" size="ctaLg">
              <Link to={routeTo(CONTACT_PATH)}>Contact the Park</Link>
            </Button>
          </div>

          <p
            className="mt-6 max-w-xl text-sm leading-relaxed text-cream/70"
            style={{ "--stagger": 5 } as React.CSSProperties}
          >
            Full-hookup RV sites, cabins, motel rooms, efficiency accommodations and tent camping
            near Falcon Lake.
          </p>
        </div>
      </div>
    </section>
  );
}
